import {ApiError} from "../client";
import {messageForCode} from "../error-codes";
import {externalCommandErrorMessage} from "../external-command-errors";
import type {ErrorCode} from "../../generated/openapi-contract";

// Тело ошибки бэкенда — ErrResponse {status, code?, error?}
// (internal/domain/errors.go): status — краткий статус-текст
// ("Invalid request." / "Forbidden." / "Conflict." и т.д.),
// code — код отказа (см. apiErrorCode), error — детальное сообщение для
// отладки (может отсутствовать).

/** Распарсенное тело ошибки бэкенда (`ErrResponse`). */
export interface ApiErrorBody {
    status?: string;
    code?: string;
    message?: string;
    error?: string;
}

/** Достать {status, error} из тела ApiError; null если тело не JSON. */
export function extractApiErrorBody(err: ApiError): ApiErrorBody | null {
    if (!err.body) return null;
    try {
        return JSON.parse(err.body) as ApiErrorBody;
    } catch {
        return null;
    }
}

const exchangeErrors: Partial<Record<ErrorCode, string>> = {
    exchange_sell_close_not_allowed: "Закрытие недоступно: заявка уже завершена или по ней есть несовместимые денежные операции.",
    order_not_disputed: "Сначала откройте спор по заявке.",
    exchange_sell_address_unavailable: "Нет свободного TRC20-адреса. Попросите администратора добавить или включить адрес для вас.",
    exchange_sell_address_invalid: "Адрес недоступен. Попросите администратора проверить адрес и его владельца.",
    exchange_sell_address_already_published: "Адрес уже выдан. Обновите заявку.",
    exchange_sell_stage_not_found: "Этап продажи USDT не найден. Обновите заявку.",
    exchange_sell_deposit_required: "Поступление USDT ещё не подтверждено. Дождитесь подтверждения или откройте спор.",
    exchange_sell_completion_forbidden: "Выплата заблокирована. Администратор должен проверить поступление и снять блок.",
    exchange_sell_amount_revision_conflict: "Условия заявки изменились. Обновите дело и повторите действие.",
    exchange_sell_amount_command_conflict: "Это решение уже отправлено с другими условиями. Обновите дело.",
    exchange_sell_amount_adjustment_forbidden: "Согласование суммы сейчас недоступно. Проверьте состояние заявки.",
    exchange_sell_amount_evidence_mismatch: "Новая сумма не соответствует подтверждённому поступлению USDT.",
    exchange_stage_not_found: "Этап отправки USDT не найден. Обновите заявку.",
    rub_not_confirmed: "Сначала подтвердите получение RUB.",
    payout_already_closed: "Отправка USDT уже завершена. Обновите заявку.",
    payout_already_sent: "Отправка уже зарегистрирована. Ожидается подтверждение сети.",
    tx_hash_invalid: "Укажите хеш TRON-транзакции: 64 символа, цифры и буквы a–f.",
};

export function exchangeErrorMessage(err: ApiError): string | undefined {
    return messageForCode(err, exchangeErrors);
}

const adminErrors: Partial<Record<ErrorCode, string>> = {
    merchant_portal_requisites_issued: "Реквизиты уже выданы мерчанту. Вернуть заявку в пул нельзя: она остаётся у текущего трейдера.",
    presented_receipts_required: "У платежа загружено несколько чеков — выберите те, которыми он закрывается.",
};

/**
 * Единый способ превратить ошибку запроса в человекочитаемую строку.
 * Разбирает ApiError (код отказа, затем статус 401/403/409 и тело
 * {status, error}) и прочие ошибки. Используется на всех admin-экранах (B/C).
 */
export function formatApiError(err: unknown): string {
    if (err instanceof ApiError) {
        const known = messageForCode(err, adminErrors) ?? externalCommandErrorMessage(err) ?? exchangeErrorMessage(err);
        if (known) return known;
        const parsed = extractApiErrorBody(err);
        const detail = parsed?.message?.trim() || parsed?.error?.trim();
        const statusText = parsed?.status?.trim();

        switch (err.status) {
            case 401:
                return "Требуется повторный вход.";
            case 403:
                return detail || "Недостаточно прав для этого действия.";
            case 404:
                return detail || "Объект не найден.";
            case 409:
                return detail || "Конфликт: объект уже существует или действие сейчас недоступно.";
            case 422:
                return detail || statusText || "Запрос некорректен.";
            case 429:
                return "Слишком много запросов. Попробуйте позже.";
            default:
                if (err.status >= 500) {
                    return "Ошибка сервера. Попробуйте позже.";
                }
                return detail || statusText || err.message || "Не удалось выполнить запрос.";
        }
    }
    if (err instanceof Error) return err.message;
    return "Не удалось выполнить запрос.";
}
